import DatabaseModel from './DatabaseModel.js';
import cerrarSesionPor401, { tokenDeLaSesion, CERRADA, OTRA_SESION } from '../utils/cierreDeSesionPor401.js';

// Adapter de "Leer boletines" (Ajustes · Festivos): sube el PDF del boletín y recoge lo que la
// IA propone. No hay lectura por enlace a propósito: el servidor no sale a internet, así que la
// URL del boletín viaja solo como referencia para guardarla junto al festivo.
var RhFestivo = Object.create(DatabaseModel);

// Misma lista que DatabaseModel.sendRequest: 524/522/520 son de Cloudflare, el 0 es la conexión
// cortada sin respuesta. En todos ellos la petición se cerró por arriba, no por el servidor.
function esTimeoutDePasarela(status) {
    return [408, 504, 520, 522, 524, 598, 599].indexOf(status) !== -1 || status === 0;
}

// El mismo Authorization que pone sendRequest en cada petición, y por el mismo lector
// (`tokenDeLaSesion`). Hace falta repetirlo aquí porque esta subida va por su cuenta y usaría la
// cabecera global de Framework7.request.setup, que se queda con el token viejo cuando la sesión se
// renueva sin recargar la aplicación (Cordova, al volver de una notificación: se guarda la sesión
// nueva pero no se vuelve a llamar a setup). Con el token caducado el servidor devuelve 401 y la
// pantalla echaba al usuario teniendo sesión válida.
function cabeceraDeSesion() {
    const token = tokenDeLaSesion();
    return token ? { Authorization: 'Bearer ' + token } : {};
}

RhFestivo.model = 'rh/festivos';
RhFestivo.modelFront = 'festivos';

/**
 * Sube el PDF del boletín y devuelve el resultado de la lectura:
 * { idLectura, modelo, propuestos[], yaExistentes[], discrepancias[], noReconocidos[],
 *   municipiosSinLocales[], error }
 *
 * Multipart a mano (patrón ItemDocumento.procesar): el navegador pone el boundary.
 */
RhFestivo.leerBoletin = function(file, anio, fuenteRef, fuenteUrl) {
    var self = this;
    var data = new FormData();
    data.append('file', file);
    data.append('anio', anio);
    data.append('fuenteRef', fuenteRef || '');
    if (fuenteUrl) data.append('fuenteUrl', fuenteUrl);
    var peticion = {
        url: self.server_movilidad + self.prefix + self.model + '/boletin/leer',
        method: 'POST',
        crossDomain: true,
        dataType: 'json',
        data: data
    };
    // Solo si hay token: `headers` sustituye entero al global de Framework7, así que mandar un
    // objeto vacío dejaría la petición sin Authorization ninguno.
    var cabecera = cabeceraDeSesion();
    if (cabecera.Authorization) peticion.headers = cabecera;
    // Token con el que sale la petición. La lectura puede tardar minutos (la IA analiza el PDF),
    // así que la sesión puede renovarse —o entrar otro usuario— mientras está en vuelo.
    const tokenEnviado = tokenDeLaSesion();
    return new Promise(function(resolve, reject) {
        self.request.promise(peticion).then(function({ data }) {
            if (data.error) { reject(data.error); } else { resolve(data.dto || data); }
        }).catch(function(fallo) {
            // Se conserva el status HTTP: es lo único que distingue el 403 del gate de rol de un
            // fallo cualquiera, y la pantalla lo necesita para enseñar «no autorizado» en vez de
            // un diálogo de error genérico.
            var xhr = fallo && fallo.xhr;
            var status = (fallo && fallo.status) || (xhr && xhr.status);
            // Esta petición no pasa por sendRequest (multipart a mano), así que el aviso de
            // timeout de pasarela que hace allí hay que repetirlo aquí. Importa más que en otras
            // pantallas: el POST lanza la IA y abre el apunte de rh_audit, de modo que el
            // servidor puede terminar la lectura después de que se corte la conexión y
            // reintentarla dejaría dos lecturas con las mismas propuestas.
            if (esTimeoutDePasarela(status)) {
                reject({
                    status: status,
                    xhr: xhr,
                    timeout: true,
                    message: 'La lectura del boletín ha tardado demasiado y la conexión se ha cerrado. '
                        + 'El servidor puede seguir analizando el PDF: la lista de «Lecturas anteriores» se '
                        + 'va actualizando sola durante un minuto, y ahí abajo tienes el botón para volver a '
                        + 'consultarla. No subas otra vez el mismo PDF hasta comprobarlo, o se repetirá el trabajo.'
                });
                return;
            }
            var cuerpo = null;
            try { cuerpo = JSON.parse(xhr.response); } catch (e) { /* respuesta no JSON */ }
            var error = (cuerpo && cuerpo.error) || {};
            // Token caducado mientras el diálogo estaba abierto. sendRequest cierra la sesión y
            // manda al login; al ir esta petición por su cuenta hay que repetirlo, o la pantalla
            // se queda con una sesión inválida dejando reintentar lecturas que ya no pueden ir.
            //
            // Pero solo si el token que sigue guardado es el que salió con la petición: cuando la
            // sesión se ha renovado mientras la IA leía el PDF, el 401 es del token viejo y
            // cerrar la sesión echaría a quien acaba de entrar. La comprobación de `generacion`
            // del componente no llega a tiempo: se hace después de este rechazo.
            //
            // Lo decide `cierreDeSesionPor401`, la misma pieza que usa el manejador de 401 de
            // `sendRequest`: la comprobación nació aquí y estaba solo aquí, que es por lo que
            // faltaba en el camino por el que van todas las demás peticiones.
            var app = window.app;
            if (status == 401 || error === 'invalid_token') {
                const desenlace = cerrarSesionPor401(tokenEnviado);
                if (desenlace === OTRA_SESION) {
                    reject({
                        status: status,
                        xhr: xhr,
                        message: 'La sesión se ha renovado mientras se leía el boletín y la lectura se ha '
                            + 'quedado sin permiso. Vuelve a subir el PDF: no se ha guardado nada.'
                    });
                    return;
                }
                if (desenlace === CERRADA) {
                    app.preloader.hide();
                    reject({ status: status, xhr: xhr, sesionCaducada: true, message: 'La sesión ha caducado.' });
                    return;
                }
            }
            reject({
                status: status,
                xhr: xhr,
                message: error.message || (xhr && xhr.response) || 'No se ha podido leer el boletín.'
            });
        });
    });
};

/** Lecturas ya hechas, de la más reciente a la más antigua. */
RhFestivo.lecturas = function(anio) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/boletin/lecturas'
            + (anio ? '?anio=' + encodeURIComponent(anio) : ''),
        method: 'GET'
    }, false).then(function(res) {
        return (res && res.list) || [];
    });
};

/** Resultado completo de una lectura, para volver a ver sus avisos sin repetirla. */
RhFestivo.lectura = function(idLectura) {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/boletin/lecturas/'
            + encodeURIComponent(idLectura),
        method: 'GET'
    }, false).then(function(res) {
        // sendRequest ya devuelve el dto desempaquetado cuando la respuesta trae {dto: …}; el
        // `res.dto` de más dejaba la pantalla de la lectura anterior en blanco.
        return (res && res.dto) || res;
    });
};

/** Municipios donde vive la plantilla: el universo que se busca en el boletín. */
RhFestivo.municipiosPlantilla = function() {
    var self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/municipios-plantilla',
        method: 'GET'
    }, false).then(function(res) {
        return (res && res.list) || [];
    });
};

export default RhFestivo;
